import React, {
	useCallback,
	useMemo,
	useState,
	useEffect,
	useRef,
} from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { store } from "../../../app/store";

// mui components
import { Box, Divider, Stack } from "@mui/material";

// custom components
import CustomTable from "../../custom-table";
import SingleSelect from "../../custom-select/single-select";
import Heading5 from "../../typography/heading-05";
import Heading6 from "../../typography/heading-06";
import Body2 from "../../typography/body-02";
import EnhancedConditionTracking from "./enhanced-condition-tracking";
import EnhancedConditionDocumentsListCountTable from "./enhanced-conditions-documents-list-count";

// mui icons
// api slice
import { useUnderwritingConditionMutation } from "../../../features/underwriting-conditions/underwritingApiSlice";
import { usePreliminaryConditionMutation } from "../../../features/preliminary-conditions/preliminaryApiSlice";
import { usePostClosingConditionMutation } from "../../../features/post-closing-conditions/postClosingApiSlice";
import { getMimeTypeFromFilename, GENERIC_FALLBACK_CONTENT_TYPE, GENERIC_FALLBACK_FILE_NAME } from "../../../utils/document-validation";
import { useConditionStatusMutation } from "../../../features/condition-status/conditionStatusApiSlice";
import { useDownloadAttachmentMutation } from "../../../features/download-attachment/downloadAttachmentApiSlice";
import { useEnhancedConditionsDocumentsListByCategoryMutation } from "../../../features/fetch-enhanced-conditions-documents-list-by-category/fetchEnhancedConditionsDocumentsListByCatgoryApiSlice";
import { useEnhancedConditionsEfolderDocumentsListMutation } from "../../../features/enhanced_conditions_efolder_documents_list/fetchEnhancedConditionsEfolderDocumentsListApiSlice";
import { useEnhancedConditionsEfolderDocumentsDownloadUrlsListMutation } from "../../../features/enhanced_conditions_documents_downloadurls/fetchEnhancedConditionsEfolderDocumentsDownloadUrlsListApiSlice";
import { useEnhancedConditionDownloadFileFromDownloadUrlMutation } from "../../../features/enhanced_conditions_documents_downloadurls/fetchEnhancedConditionsEfolderDocumentDownloadFileFromDownloadUrlApiSlice";
import {
	waitForPendingApiRequestsToSettle,
} from "../../../features/api-request-status/apiRequestStatusSlice";

// reducer slice
import { setCategoryCondition } from "../../../features/category-condition/categoryConditonSlice";
import { setEnhancedConditionStatus } from "../../../features/enhanced-condition-status/enhancedConditionStatusSlice";
import { selectUnderwritingConditions } from "../../../features/underwriting-conditions/underwritingSlice";
import { selectPreliminaryConditions } from "../../../features/preliminary-conditions/preliminarySlice";
import { selectPostClosingConditions } from "../../../features/post-closing-conditions/postClosingSlice";
import { selectConditionStatus } from "../../../features/condition-status/conditionStatusSlice";
import { selectEnhancedConditionDocumentsListByCategories } from "../../../features/fetch-enhanced-conditions-documents-list-by-category/fetchEnhancedConditionsDocumentsListByCategory";

// redux functions
import { getUnderwritingConditions } from "../../../app/services/underwriting-conditions";
import { getPreliminaryConditions } from "../../../app/services/preliminary-conditions";
import { getPostClosingConditions } from "../../../app/services/post-closing-conditions";
import { getConditionStatus } from "../../../app/services/condition-status";
import { getEnhancedConditionsDocumentsListByCategory } from "../../../app/services/enhanced-conditions-documents-lists-by-category";

// helper utils
import useDownloadBase64PDF from "../../../utils/convert-base64";
import { filterValue } from "../../../utils/filter-value";

// custom styles
import styles from "./index.module.scss";
import Heading4 from "../../typography/heading-04";
import { useGetAttachmentsMutation } from "../../../features/get-attachments/getAttachmentsApiSlice";

const openSummarySubStatuses = new Set([
	"added",
	"received",
	"requested",
	"re-requested",
	"rerequested",
	"rejected",
	"reviewed",
]);

const closedSummarySubStatuses = new Set([
	"cleared",
	"waived",
	"fulfilled",
]);

const ConditionTrackingCard = ({
	toggleDrawer,
	fieldData,
	// loading modal
	handleOpenLoadingDataModal,
	handleCloseLoadingDataModal,
	setLoadingDataText,
	// error modal
	handleOpenCommonErrorModal,
	// custom functions
	setSelectedCondition,
}) => {
	const params = useParams();
	const [conditionCategory, setConditionCategory] = useState("");
	const [conditionStatus] = useConditionStatusMutation();
	const [underwritingCondition] = useUnderwritingConditionMutation();
	const [preliminaryCondition] = usePreliminaryConditionMutation();
	const [postClosingCondition] = usePostClosingConditionMutation();
	const [downloadAttachment] = useDownloadAttachmentMutation();
	const [getAttachments] = useGetAttachmentsMutation();
	const [enhancedConditionsRequest] =
		useEnhancedConditionsDocumentsListByCategoryMutation();
	const [enhancedConditionsEfolderDocumentsList] =
		useEnhancedConditionsEfolderDocumentsListMutation();
	const [enhancedConditionsEfolderDocumentsDownloadUrlsList] =
		useEnhancedConditionsEfolderDocumentsDownloadUrlsListMutation();
	const [enhancedConditionDownloadFileFromDownloadUrl] =
		useEnhancedConditionDownloadFileFromDownloadUrlMutation();

	const { downloadConvertedFile } = useDownloadBase64PDF();
	const enhancedConditionsData = useSelector(
		selectEnhancedConditionDocumentsListByCategories
	);
	const [documentsList, setDocumentsList] = useState([]);
	const [isConditionStatusLoaded, setIsConditionStatusLoaded] = useState(false);
	const [isEnhancedDocumentsLoading, setIsEnhancedDocumentsLoading] =
		useState(true);
	const requestHistoryRef = useRef(new Set());
	const conditionListRefreshRevisionRef = useRef(0);
	const conditionListRefreshResolverRef = useRef(null);
	const [conditionListRefreshRevision, setConditionListRefreshRevision] =
		useState(0);

	const handleConditionListRefreshApplied = useCallback((appliedRevision) => {
		if (appliedRevision !== conditionListRefreshRevisionRef.current) {
			return;
		}

		const resolve = conditionListRefreshResolverRef.current;
		conditionListRefreshResolverRef.current = null;
		resolve?.();
	}, []);

	/*ENHANCED CONDITIONS */
	const hasEnhancedConditionsFieldData =
		Array.isArray(fieldData) && fieldData.length > 0;
	const enhancedConditionsStatus = hasEnhancedConditionsFieldData
		? String(filterValue(fieldData, "ENHANCEDCOND.X1") ?? "")
				.trim()
				.toUpperCase()
		: null;

	const updateDocumentsList = useCallback(
		(nextDocuments) => {
			setDocumentsList(nextDocuments);
		},
		[enhancedConditionsStatus]
	);

	useEffect(() => {
		if (!hasEnhancedConditionsFieldData) return;

		store.dispatch(setEnhancedConditionStatus(enhancedConditionsStatus || ""));
	}, [enhancedConditionsStatus, hasEnhancedConditionsFieldData]);

	const handleErrorModal = useCallback(
		(errorKey, data) => {
			handleCloseLoadingDataModal();
			handleOpenCommonErrorModal(errorKey, data?.data);
		},
		[handleCloseLoadingDataModal, handleOpenCommonErrorModal]
	);

	const brokerUploadColumns = [
		{ id: "condition", label: "Condition", minWidth: 120 },
		{ id: "status", label: "Status", minWidth: 120 },
		{ id: "category", label: "Category", minWidth: 120 },
		{ id: "prior to", label: "Prior to", minWidth: 150 },
		{ id: "source", label: "Source", minWidth: 100, align: "center" },
		{ id: "added", label: "Added", minWidth: 100, align: "center" },
		{ id: "docs", label: "Docs", minWidth: 150 },
	];

	// get underwriting conditions data from redux store
	const underwritingConditionData = useSelector(selectUnderwritingConditions);
	// get preliminary conditions data from redux store
	const preliminaryConditionData = useSelector(selectPreliminaryConditions);
	// get post closing conditions data from redux store
	const postClosingConditionData = useSelector(selectPostClosingConditions);

	const loadEnhancedConditionsDocumentsListOnCategory =
		useCallback(async () => {
			if (!params?.id || conditionCategory !== "All") return;

			setIsEnhancedDocumentsLoading(true);
			setIsConditionStatusLoaded(false);

			try {
				await getEnhancedConditionsDocumentsListByCategory({
					loanGuid: params?.id,
					conditionType: "All",
					apiRequest: enhancedConditionsRequest,
					handleErrorModal,
				});
			} catch (err) {
				handleErrorModal("ENHANCED_CONDITION_DOCUMENTS_UNAVAILABLE", err);
				throw err;
			} finally {
				setIsEnhancedDocumentsLoading(false);
				setIsConditionStatusLoaded(true);
			}
		}, [
			conditionCategory,
			enhancedConditionsRequest,
			handleErrorModal,
			params?.id,
		]);

	useEffect(() => {
		if (enhancedConditionsStatus !== "Y") return;

		if (conditionCategory !== "All") {
			setConditionCategory("All");
			store.dispatch(setCategoryCondition("All"));
		}
	}, [enhancedConditionsStatus, conditionCategory]);

	// run api and update state based on selected condition category
	useEffect(() => {
		if (enhancedConditionsStatus === "Y") {
			void loadEnhancedConditionsDocumentsListOnCategory().catch(() => {});
			return;
		}

		(async () => {
			try {
				await Promise.all([
					getUnderwritingConditions({
						loanGuid: params?.id,
						apiRequest: underwritingCondition,
						handleErrorModal,
					}),
					getPreliminaryConditions({
						loanGuid: params?.id,
						apiRequest: preliminaryCondition,
						handleErrorModal,
					}),
					getPostClosingConditions({
						loanGuid: params?.id,
						apiRequest: postClosingCondition,
						handleErrorModal,
					}),
				]);
			} finally {
				handleCloseLoadingDataModal?.();
				setIsConditionStatusLoaded(true);
				setIsEnhancedDocumentsLoading(false);
			}
		})();
	}, [
		enhancedConditionsStatus,
		handleCloseLoadingDataModal,
		handleErrorModal,
		handleOpenLoadingDataModal,
		loadEnhancedConditionsDocumentsListOnCategory,
		params?.id,
		postClosingCondition,
		preliminaryCondition,
		setLoadingDataText,
		underwritingCondition,
	]);

	useEffect(() => {
		if (enhancedConditionsStatus === "Y") return;
		const nextDocuments = [
			...(underwritingConditionData || []),
			...(preliminaryConditionData || []),
			...(postClosingConditionData || []),
		];
		updateDocumentsList(nextDocuments);
	}, [
		enhancedConditionsStatus,
		postClosingConditionData,
		preliminaryConditionData,
		underwritingConditionData,
		updateDocumentsList,
	]);

	const refreshEnhancedConditionsList = useCallback(async () => {
		if (enhancedConditionsStatus !== "Y" || !params?.id) {
			return;
		}

		handleOpenLoadingDataModal?.();
		setLoadingDataText?.("Setting up the conditions list");

		try {
			await loadEnhancedConditionsDocumentsListOnCategory();

			const nextRevision = conditionListRefreshRevisionRef.current + 1;
			conditionListRefreshRevisionRef.current = nextRevision;

			const refreshAppliedPromise = new Promise((resolve) => {
				conditionListRefreshResolverRef.current = resolve;
			});

			setConditionListRefreshRevision(nextRevision);
			await refreshAppliedPromise;

			await waitForPendingApiRequestsToSettle(store);
		} catch (error) {
			conditionListRefreshResolverRef.current = null;
			throw error;
		} finally {
			handleCloseLoadingDataModal?.();
		}
	}, [
		enhancedConditionsStatus,
		handleCloseLoadingDataModal,
		handleOpenLoadingDataModal,
		loadEnhancedConditionsDocumentsListOnCategory,
		params?.id,
		setLoadingDataText,
	]);

	const effectiveDocumentsList = useMemo(() => {
		const nextDocuments =
			enhancedConditionsStatus === "Y"
				? enhancedConditionsData || []
				: documentsList;
		return nextDocuments;
	}, [documentsList, enhancedConditionsData, enhancedConditionsStatus]);

	const derivedSummaryCounts = useMemo(
		() =>
			(effectiveDocumentsList || []).reduce(
				(acc, row) => {
					const status = String(row?.sub_status ?? "")
						.trim()
						.toLowerCase();

					acc.total += 1;

					if (openSummarySubStatuses.has(status)) {
						acc.openRequested += 1;
					} else if (closedSummarySubStatuses.has(status)) {
						acc.fulfilled += 1;
						if (status === "waived") {
							acc.waived += 1;
						}
					}

					return acc;
				},
				{
					total: 0,
					openRequested: 0,
					fulfilled: 0,
					waived: 0,
				}
			),
		[effectiveDocumentsList]
	);
	
	const summaryCountsToDisplay = derivedSummaryCounts;

	// get condition status data from redux store
	const conditionStatusData = useSelector(selectConditionStatus);

	if (!hasEnhancedConditionsFieldData) {
		return null;
	}

	// update condition category drodpown state
	const handleConditionCategory = (event) => {
		setConditionCategory(event.target.value);
		store.dispatch(setCategoryCondition(event.target.value));
	};

	const handleConditionUploadClick = (event, id) => {
		// Create a copy of the existing array and add the new string
		const newArray = [id];
		// Update the state with the new array
		setSelectedCondition(newArray);

		toggleDrawer(event, "condition-tracking");
	};

	const handleConditionDownloadClick = async (event, id) => {
		if (id === undefined || id?.length === 0 || id === "" || id === null) {
			handleCloseLoadingDataModal();
			handleOpenCommonErrorModal("CONDITION_TRACKING_ATTACHMENT_ERROR");
			return;
		}
		// No Document found for download
		setLoadingDataText("Getting the loan documents");
		handleOpenLoadingDataModal();

		const attachmentFormData = new FormData();
		attachmentFormData.append("loan_guid", params?.id);

		try {
			if (enhancedConditionsStatus === "Y") {
				const efolderResponse = await enhancedConditionsEfolderDocumentsList({
					loanGuid: params?.id,
				}).unwrap();

				const efolderDocuments = Array.isArray(efolderResponse)
					? efolderResponse
					: efolderResponse?.data || [];

				const selectedIds = (Array.isArray(id) ? id : [id])
					.filter((item) => item !== undefined && item !== null)
					.map(String);

				const matchedDocuments = efolderDocuments.filter((doc) => {
					const docIds = [doc?.assignedTo?.entityId]
						.filter(Boolean)
						.map(String);

					return docIds.some((docId) => selectedIds.includes(docId));
				});
				//get all ids in an array from matchDosumnts
				const attachmentIds = matchedDocuments.map((doc) => doc.id);
				//get download urls for matched documents
				const downloadUrlsResponse =
					await enhancedConditionsEfolderDocumentsDownloadUrlsList({
						loanGuid: params?.id,
						attachmentIds: attachmentIds,
					}).unwrap();

				for (const urlData of downloadUrlsResponse?.data?.attachments || []) {
					try {
						const downloadFileResponse =
							await enhancedConditionDownloadFileFromDownloadUrl({
								downloadUrl: urlData?.url,
								authorization: urlData?.authorizationHeader,
							}).unwrap();
						const fileName =
							urlData?.title ||
							matchedDocuments.find(
								(doc) =>
									String(doc?.id) === String(urlData?.id) ||
									String(doc?.id) === String(urlData?.attachmentId) ||
									String(doc?.id) === String(urlData?.entityId)
							)?.title ||
							GENERIC_FALLBACK_FILE_NAME;

						downloadConvertedFile(
							downloadFileResponse,
							downloadFileResponse?.type ||
								getMimeTypeFromFilename(fileName) ||
								GENERIC_FALLBACK_CONTENT_TYPE,
							fileName
						);
						handleCloseLoadingDataModal();
					} catch (err) {
						handleCloseLoadingDataModal();
					}
				}
			} else {
				const attachmentData = await getAttachments({
					formData: attachmentFormData,
				}).unwrap();
				const attachmentList = attachmentData?.response;

				const matchedIds = attachmentList
					.filter((item) => id.includes(item.assignedTo?.entityId))
					.map((item) => item.id);
				if (matchedIds?.length === 0) {
					handleCloseLoadingDataModal();
					handleOpenCommonErrorModal("CONDITION_DOCUMENT_UNAVAILABLE");
					return;
				}
				var formData = new FormData();
				formData.append("loan_guid", params?.id);
				formData.append("attachment_ids", matchedIds);

				try {
					const downloadAttachmentData = await downloadAttachment({
						formData,
					}).unwrap();

					let attachmentArr = downloadAttachmentData?.response?.attachments;
					if (attachmentArr?.length > 0) {
						for (let index = 0; index < attachmentArr?.length; index++) {
							if (attachmentArr[index]?.authorizationHeader) {
								const fileName =
									attachmentList.find(
										(item) => item.entityId === attachmentArr[index].id
									)?.entityName ||
									attachmentList.find(
										(item) => item.id === attachmentArr[index].id
									)?.title ||
									"";

								downloadConvertedFile(
									attachmentArr[index]?.b64,
									attachmentArr[index]?.contentType,
									fileName || "download"
								);
								handleCloseLoadingDataModal();
							} else {
								if (attachmentArr[index]?.url !== undefined) {
									handleCloseLoadingDataModal();
									window.open(attachmentArr[index]?.url, "_blank")?.focus();
								} else if (
									attachmentArr[index]?.originalUrls[0] !== undefined
								) {
									handleCloseLoadingDataModal();
									window
										.open(attachmentArr[index]?.originalUrls[0], "_blank")
										?.focus();
								}
							}
						}
					} else {
						handleCloseLoadingDataModal();
						handleErrorModal(downloadAttachmentData?.response?.details);
					}
				} catch (err) {
					handleCloseLoadingDataModal();
					handleErrorModal(err);
				}
			}
		} catch (err) {
			handleCloseLoadingDataModal();
			handleErrorModal(err);
		}
	};

	return (
		<Stack direction="column" className={styles.stackContainer}>
			<Stack direction="column" className={styles.conditionHeaderContainer}>
				<Stack direction="column" className={styles.conditionStatusContainer}>
					<Heading6
						text="Conditions status"
						fontType="semibold"
						extraClass={styles.statusHeader}
					/>

					<EnhancedConditionDocumentsListCountTable
						documentListCount={summaryCountsToDisplay}
						isLoading={!isConditionStatusLoaded || isEnhancedDocumentsLoading}
					/>
				</Stack>
			</Stack>
			<EnhancedConditionTracking
				documentsList={effectiveDocumentsList}
				brokerUploadColumns={brokerUploadColumns}
				setDocumentsList={updateDocumentsList}
				handleConditionUploadClick={handleConditionUploadClick}
				handleConditionDownloadClick={handleConditionDownloadClick}
				toggleDrawer={toggleDrawer}
				loanId={params?.id}
				onConditionListRefresh={refreshEnhancedConditionsList}
				conditionListRefreshRevision={conditionListRefreshRevision}
				onConditionListRefreshApplied={handleConditionListRefreshApplied}
			/>
		</Stack>
	);
};

export default ConditionTrackingCard;
